import { Show, useObservable } from "@legendapp/state/react"
import MIcon from "@react-native-vector-icons/material-design-icons"
import { cn, ENV, sharedStyles, trpc } from "@src/utils"
import { ActivityIndicator, Modal, Platform, Pressable, View } from "react-native"
import { Text, TextInput } from "./core"
import { SafeAreaView, useSafeAreaInsets } from "react-native-safe-area-context"
import { useState } from "react"
import { List } from "./list"
import { ar } from "@src/i18n/ar"
import { useNumCols } from "@src/hooks"
import { Image } from "expo-image"

type PickerProps = { value?: number; onChange: (id: number) => void; className?: string }

export function SectionPicker({ value, onChange, className }: PickerProps) {
  const sections = trpc.menu.sections.useQuery()
  const selectedSection = sections.data?.find((s) => s.id === value)
  const visible = useObservable(false)

  return (
    <>
      <Pressable
        className={cn("min-h-[45px] bg-white border border-gray-300 rounded px-4 justify-center active:opacity-70 flex-row items-center", className)}
        onPress={() => visible.set(true)}
      >
        <Text className={cn("flex-1", !selectedSection?.name && "text-gray-400")}>{selectedSection?.name ?? ar.selectSection}</Text>
        <View className="absolute left-3">
          <MIcon name="chevron-down" size={24} color="#999" />
        </View>
      </Pressable>

      <Show if={visible}>
        <SectionPickModal value={value} onChange={onChange} hide={() => visible.set(false)} />
      </Show>
    </>
  )
}

function SectionPickModal({ value, onChange, hide }: PickerProps & { hide: () => void }) {
  const insets = useSafeAreaInsets()
  const [searchText, setSearchText] = useState("")
  const sections = trpc.menu.sections.useQuery()
  const numCols = Math.max(2, useNumCols())

  const filteredSections = sections.data?.filter((s) => s.name.toLowerCase().includes(searchText.toLowerCase())) || []

  return (
    <Modal animationType="slide" presentationStyle="formSheet" onRequestClose={hide}>
      <View className="flex-1 bg-white">
        <View className="flex-row py-3 px-4 items-center justify-between border-b border-gray-100" style={{ marginTop: Platform.OS === "android" ? insets.top : 0 }}>
          <Pressable onPress={hide} hitSlop={12}>
            <MIcon name="close" size={24} color="#333" />
          </Pressable>
          <Text className="text-lg font-bold text-gray-800">{ar.selectSection}</Text>
          <View className="w-6" />
        </View>

        <TextInput autoFocus value={searchText} onChangeText={setSearchText} placeholder={ar.search} className="mb-2 mx-4" />
        <List
          data={filteredSections}
          numColumns={numCols}
          contentContainerStyle={sharedStyles.contentContainer}
          key={`sections-${sections.data?.length}`}
          keyExtractor={(item) => item.id.toString()}
          ListEmptyComponent={() => {
            return sections.isLoading ? <ActivityIndicator size="small" className="py-2" /> : <Text className="text-center py-8 text-gray-500">{ar.noSections}</Text>
          }}
          renderItem={({ item }) => {
            const image = item.images?.at(-1)

            return (
              <Pressable
                className={cn("p-2 border-b border-gray-100 active:bg-gray-50 justify-center items-center", value === item.id && "bg-blue-50")}
                onPress={() => {
                  onChange(item.id)
                  hide()
                }}
              >
                {image ? (
                  <Image source={`${ENV.R2_URL}/${image.key}`} style={{ width: 100, height: 100, marginBottom: 8 }} transition={250} loading="lazy" cachePolicy="memory" />
                ) : (
                  <View style={{ width: 100, height: 100, marginBottom: 8 }} />
                )}
                <Text className="text-sm font-medium text-gray-800">{item.name}</Text>
              </Pressable>
            )
          }}
          ListFooterComponent={<SafeAreaView edges={["bottom"]} />}
        />
      </View>
    </Modal>
  )
}
